import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    Coupon,
    CreateCouponInput,
    UpdateCouponInput,
    DeleteCouponInput,
    CouponsByCodeQueryVariables,
    CouponsByStatusQueryVariables,
    CouponsByCreatedByQueryVariables
} from '@app/core/services/api/API.service';

@Injectable({
    providedIn: 'root'
})
export class CouponService {
    private amplifyClient = generateClient();

    constructor() {}

    async listCoupons(): Promise<Coupon[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listCoupons
                })
            ).data.listCoupons.items;
            if (data) return data as Coupon[];
        } catch (error) {
            console.error('Error listing coupons:', error);
            return null;
        }
    }

    async getCoupon(id: string): Promise<Coupon> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.getCoupon,
                    variables: { id }
                })
            ).data.getCoupon;
            if (data) return data as Coupon;
        } catch (error) {
            console.error('Error getting coupon:', error);
            return null;
        }
    }

    async createCoupon(input: CreateCouponInput): Promise<Coupon> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createCoupon,
                    variables: {
                        input
                    }
                })
            ).data.createCoupon;
            if (data) return data as Coupon;
        } catch (error) {
            console.error('Error creating coupon:', error);
            return null;
        }
    }

    async updateCoupon(input: UpdateCouponInput): Promise<Coupon> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.updateCoupon,
                    variables: {
                        input
                    }
                })
            ).data.updateCoupon;
            if (data) return data as Coupon;
        } catch (error) {
            console.error('Error updating coupon:', error);
            return null;
        }
    }

    async deleteCoupon(input: DeleteCouponInput): Promise<Coupon> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteCoupon,
                    variables: {
                        input
                    }
                })
            ).data.deleteCoupon;
            if (data) return data as Coupon;
        } catch (error) {
            console.error('Error deleting coupon:', error);
            return null;
        }
    }

    async getCouponsByCode(
        code: string,
        variables?: Omit<CouponsByCodeQueryVariables, 'code'>
    ): Promise<Coupon[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.couponsByCode,
                    variables: {
                        code,
                        ...variables
                    }
                })
            ).data.couponsByCode.items;
            if (data) return data as Coupon[];
        } catch (error) {
            console.error('Error getting coupons by code:', error);
            return null;
        }
    }

    async getCouponsByStatus(
        status: string,
        variables?: Omit<CouponsByStatusQueryVariables, 'status'>
    ): Promise<Coupon[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.couponsByStatus,
                    variables: {
                        status,
                        ...variables
                    }
                })
            ).data.couponsByStatus.items;
            if (data) return data as Coupon[];
        } catch (error) {
            console.error('Error getting coupons by status:', error);
            return null;
        }
    }

    async getCouponsByCreatedBy(
        createdBy: string,
        variables?: Omit<CouponsByCreatedByQueryVariables, 'createdBy'>
    ): Promise<Coupon[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.couponsByCreatedBy,
                    variables: {
                        createdBy,
                        ...variables
                    }
                })
            ).data.couponsByCreatedBy.items;
            if (data) return data as Coupon[];
        } catch (error) {
            console.error('Error getting coupons by createdBy:', error);
            return null;
        }
    }
}
